import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import QrReader from 'react-qr-reader'
import Column from '@amzn/meridian/column'
import Select, { SelectOption } from '@amzn/meridian/select'
import Heading from '@amzn/meridian/heading'
import Button from '@amzn/meridian/button'
import Loader from '@amzn/meridian/loader'
import _ from 'lodash'
import { StoreIdServiceFor3P } from '@src/pages/StoreIdSelectionFor3P/utils/StoreIdServiceFor3P'
import Text from '@amzn/meridian/text'
import Input from '@amzn/meridian/input'
import { DeviceDetails } from '@amzn/ihm-associate-tools-js-lib'

const StoreIdSelectionPageFor3P = () => {
    const navigate = useNavigate()
    const [stores, setStores] = useState([])
    const [options, setOptions] = useState()
    const [inputValue, setInputValue] = useState('')
    const [loading, setLoading] = useState(true)
    const[deviceType, setDeviceType] = useState(null)
    const [errorState, setErrorState] = useState(false)
    const [errorValue, setErrorValue] = useState(null)
    const [errorMsg, setErrorMsg] = useState(null)
    const [scanErrorState, setScanErrorState] = useState(false)
    const STORE_ID_REGEX = new RegExp('^[0-9a-zA-Z\\\\-]{1,32}$')
    const scanErrorMessage = 'Error setting store details for storeId '
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true)
                const deviceDetails: {deviceSerialNumber?: string, deviceType?: string} = await DeviceDetails.getDeviceDetails()
                setDeviceType(deviceDetails.deviceType)
                const response = await StoreIdServiceFor3P.getNearbyStoresForUser()
                setStores(response)
                setLoading(false)
                setErrorState(false)
                setErrorValue(null)
            } catch (error) {
                setStores([])
                setLoading(false)
                setErrorState(true)
                setErrorValue(error.message)
                console.error('ATBase: Error occurred while loading data ', error.message)
            }
        }
        loadData()
    }, [])

    const setStoreId = async (storeId: string, isStoreIdSetThroughQROrInput: boolean) => {
        console.log('ATBase: Setting storeId', storeId)
        try {
            setErrorValue(null)
            setScanErrorState(false)
            await StoreIdServiceFor3P.setSelectedStoreIdAndStoreDetail(storeId, isStoreIdSetThroughQROrInput)
            navigate('/provision')
        } catch (error) {
            console.error('ATBase: Exception occurred while setting selected store', error.message)
            if (isStoreIdSetThroughQROrInput) {
                setScanErrorState(true)
                setErrorMsg(scanErrorMessage + storeId)
            } else {
                setErrorValue(scanErrorMessage + storeId)
            }
            throw error
        }
    }
    const handleError = (error) => {
        console.error('ATBase: Error occurred while scanning QR code ', error)
    }

    const handleQRScan = async (storeId) => {
        if (!_.isNull(storeId) && STORE_ID_REGEX.test(storeId)) {
            await setStoreId(storeId, true)
        } else if (!STORE_ID_REGEX.test(storeId)) {
            setErrorValue('Invalid QR code')
        }
    }

    const handleOnclick = () => {
        console.log('ATBase: Redirecting User to manually enter storeId')
        navigate('/input-store-Id')
    }
    const handleOnchange = (optionStore) => {
        setOptions(optionStore)
        setStoreId(optionStore, false)
    }

    useEffect(() => {
        if (stores.length === 1) {
            handleOnchange(stores[0])
        }
    }, [stores])

    /* In TC56 devices the barcode scanner stops lighting up and scanning once camera loads
     * in the store selection screen. To circumvent this as a short term fix we're instead
     * providing a text box into which the normal 2D barcode scanner scan the QR code.
     */
    const fetchStoresLoader = (loading &&
        <Column alignmentVertical={'center'} alignmentHorizontal={'center'} height={'100%'} width={'100%'}>
            <Loader/>
            <Text type="h100" alignment="center">
                Getting nearby stores
            </Text>
        </Column>
    )

    const selectStoresDropdown = (
        <Select width={'100%'} placeholder="Select store ID" value={options} error= {errorState} onChange={(optionStore) => { handleOnchange(optionStore) }}>
        { stores.map((optionStore) => {
            return (
                <SelectOption key={optionStore} value={optionStore} label={optionStore} />
            )
        })}
        </Select>
    )

    if (deviceType === 'TC56') {
        console.log('Using TC56 device. Replacing camera scanner with text field.')
        return (
            <>
                {fetchStoresLoader}
                {!loading &&
                    <Column spacing={'medium'}>
                        <Heading level={5} alignment={'center'}> Select Store </Heading>
                        <Column spacingInset="medium">
                            {selectStoresDropdown}
                            {errorValue &&
                                <Text type="h200" alignment="center">
                                    {errorValue}
                                </Text>}
                            <Input pattern={STORE_ID_REGEX} value={inputValue} onChange={setInputValue}
                                   type={'text'} placeholder={'Scan QR Code or Manually enter Store ID'}/>
                            <Button type={'primary'} onClick={() => {
                                setStoreId(inputValue, true)
                            }}> Proceed {`>>`} </Button>
                            {scanErrorState && <Text type="h200" alignment="center">
                                {errorMsg}
                            </Text>}
                        </Column>
                    </Column>
                }
            </>
        )
    } else {
        return (
            <>
                {fetchStoresLoader}
                {!loading &&
                    <Column spacing={'medium'} >
                        <QrReader delay = {100} style = {{ width: '100%', padding: '30px' }} onError = {handleError} onScan = {handleQRScan} />
                        <Heading level={5} alignment={'center'}> Select Store </Heading>
                        <Column spacingInset="medium" >
                            {selectStoresDropdown}
                            { errorValue &&
                                <Text type="h200" alignment="center" >
                                    { errorValue }
                                </Text> }
                        </Column>
                        <Button type={'link'} size={'large'} onClick={() => { handleOnclick() }} > Manually Select Store </Button>
                    </Column>
                }
            </>
        )
    }
}

export default StoreIdSelectionPageFor3P
